import {toWidget, toWidgetEditable} from '@ckeditor/ckeditor5-widget/src/utils';

export default class ContentTemplate {

    constructor(editor, name, html) {
        this.editor = editor;
        this.name = name;
        this.html = html;
        this.models = [];

        this.dom = document.createElement('template');
        this.dom.innerHTML = html;
        this.dom = this.dom.content.firstChild;

        this._markModels(this.dom);
        this._registerModels(this.dom);
    }

    write(writer, node) {
        node = node ? node : this.dom;

        if (node.nodeName === '#text') {
            let textContent = node.textContent.trim();
            if (textContent) {
                let text = writer.createText(textContent);

                if (node.parentNode._editorContentTemplateEditable === '$root') {
                    let paragraphElement = writer.createElement('paragraph');
                    writer.append(text, paragraphElement);

                    return paragraphElement;
                }

                return text;
            }

            return null;
        }

        const element = writer.createElement(
            node._editorContentTemplateNodeModel
            ? node._editorContentTemplateNodeModel
            : this._convertTagToModel(
                node.tagName.toLowerCase()
            )
        );

        node.childNodes.forEach(childNode => {
            let childElement = this.write(writer, childNode);
            if (childElement) {
                writer.append(childElement, element);
            }
        });

        if (node._editorContentTemplateEditable === '$root' && ! node.childNodes.length) {
            let paragraphElement = writer.createElement('paragraph');
            writer.append(paragraphElement, element);
        }

        return element;
    }

    _markModels(node) {
        if (node.nodeName === '#text' || ! node.className) {
            return;
        }

        let parentModel = node.parentNode._editorContentTemplateNodeModel;
        if (! parentModel) {
            parentModel = this.name;
        }

        node._editorContentTemplateNodeModel = parentModel + '____' + node.className;

        let tag = node.tagName.toLowerCase();
        if (['div', 'section'].includes(tag) && node.dataset.editable === '$root') {
            node._editorContentTemplateEditable = '$root';
        } else if (node.dataset.editable === '$text') {
            node._editorContentTemplateEditable = '$text';
        }

        node.childNodes.forEach(childNode => {
            this._markModels(childNode);
        });
    }

    _registerModels(node) {
        if (node.nodeName === '#text' || ! node.className) {
            return;
        }

        if (this.models.includes(node._editorContentTemplateNodeModel)) {
            return;
        }

        this.models.push(node._editorContentTemplateNodeModel);

        if (node === this.dom) {
            this._registerContainerModel(node);
        } else if (node._editorContentTemplateEditable) {
            this._registerEditableModel(node);
        } else {
            this._registerNonEditableModel(node);
        }

        node.childNodes.forEach(childNode => {
            this._registerModels(childNode);
        });
    }

    _registerContainerModel(node) {
        let tag = node.tagName.toLowerCase();

        this.editor.model.schema.register(node._editorContentTemplateNodeModel, {
            allowIn: '$root',
            allowChildren: this._exportChildrenNodeModels(node),
            isObject: true,
            isLimit: true,
            isBlock: true,
        });

        this.editor.conversion
            .for('upcast')
            .elementToElement({
                view: {
                    name: tag,
                    classes: [node.className]
                },
                model: node._editorContentTemplateNodeModel
            });

        this.editor.conversion
            .for('dataDowncast')
            .elementToElement({
                model: node._editorContentTemplateNodeModel,
                view: {
                    name: tag,
                    classes: [node.className]
                }
            });

        this.editor.conversion
            .for('editingDowncast')
            .elementToElement({
                model: node._editorContentTemplateNodeModel,
                view: (modelElement, {writer: viewWriter}) => {
                    const section = viewWriter.createContainerElement(tag, {
                        class: node.className + ' editor-content-template'
                    });

                    return toWidget(section, viewWriter);
                }
            });
    }

    _registerEditableModel(node) {
        let tag = node.tagName.toLowerCase();
        let config = {
            allowIn: node.parentNode._editorContentTemplateNodeModel
        };

        if (node._editorContentTemplateEditable === '$root') {
            config.allowChildren = '$block';
            config.allowContentOf = '$root';
            config.isBlock = true;
            config.isLimit = true;
        } else {
            config.allowChildren = '$text',
            config.allowContentOf = '$text';
            config.isObject = true;
            config.isInline = true;
            config.isLimit = true;
            config.isBlock = true;
        }

        this.editor.model.schema.register(node._editorContentTemplateNodeModel, config);

        this.editor.conversion
            .for('upcast')
            .elementToElement({
                view: {
                    name: tag,
                    classes: [node.className]
                },
                model: node._editorContentTemplateNodeModel
            });

        this.editor.conversion
            .for('dataDowncast')
            .elementToElement({
                model: node._editorContentTemplateNodeModel,
                view: {
                    name: tag,
                    classes: [node.className]
                }
            });

        this.editor.conversion
            .for('editingDowncast' )
            .elementToElement({
                model: node._editorContentTemplateNodeModel,
                view: (modelElement, {writer: viewWriter}) => {
                    const element = viewWriter.createEditableElement(tag, {
                        class: node.className
                    });

                    return toWidgetEditable(element, viewWriter);
                }
            });
    }

    _registerNonEditableModel(node) {
        this.editor.model.schema.register(node._editorContentTemplateNodeModel, {
            allowIn: node.parentNode._editorContentTemplateNodeModel,
            allowChildren: this._exportChildrenNodeModels(node),
            isObject: true,
            isInline: true,
            isLimit: true,
            isBlock: true,
        });

        this.editor.conversion
            .for('upcast')
            .elementToElement({
                view: {
                    name: 'div',
                    classes: [node.className]
                },
                model: node._editorContentTemplateNodeModel
            });

        this.editor.conversion
            .for('dataDowncast')
            .elementToElement({
                model: node._editorContentTemplateNodeModel,
                view: {
                    name: 'div',
                    classes: [node.className]
                }
            });

        this.editor.conversion
            .for('editingDowncast' )
            .elementToElement({
                model: node._editorContentTemplateNodeModel,
                view: {
                    name: 'div',
                    classes: [node.className]
                }
            });
    }

    _exportChildrenNodeModels(node) {
        let children = [];
        node.childNodes.forEach(child => {
            if (child._editorContentTemplateNodeModel) {
                children.push(child._editorContentTemplateNodeModel);
            }
        });

        return children;
    }

    _convertTagToModel(tag) {
        switch (tag) {
            case 'h1':
                return 'heading1';

            case 'h2':
                return 'heading2';

            case 'h3':
                return 'heading3';

            case 'h4':
                return 'heading4';

            case 'h5':
                return 'heading5';

            case 'h6':
                return 'heading6';

            case 'p':
            default:
                return 'paragraph';
        }
    }
}
